import React, { useState } from 'react';
import { getEventCardDateLabel, getEventCardGuestLabel, TDR } from 'tdr-common';
import styles from './EventCard.module.scss';
import { DynamicIconsax } from '../icons';
import { motion } from 'framer-motion';
import ArrowDown2 from 'iconsax-react/dist/esm/ArrowDown2';
import ArrowUp2 from 'iconsax-react/dist/esm/ArrowUp2';
import { getEventCdnImageUri } from '../../helpers/getCDNURI';
import exportedConfig from '../../common/config';

/**
 * Get event cover image path
 * Tree-shaken in production builds - staging path check removed
 */
export function getEventImagePath(eventId: string): string {
	const prodPath = `/images/events/${eventId}/coverImage.jpg`;

	// Non-production environment detection
	// Tree-shaken in production builds via Webpack DefinePlugin
	if (process.env.NODE_ENV !== 'production') {
		if (exportedConfig.deployment !== 'production') {
			return `/staging/images/events/${eventId}/coverImage.jpg`;
		}
	}

	return prodPath;
}

export function formatTime(time: string): string {
	const [hours] = time.split(':').map(Number);
	const period = hours >= 12 ? 'PM' : 'AM';
	const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
	return `${formattedHours}${period}`;
}

export function formatBetweenTimes(betweenTimes: [string, string]): string {
	const [startTime, endTime] = betweenTimes;
	if (startTime === endTime) {
		return formatTime(startTime);
	}
	return `${formatTime(startTime)}-${formatTime(endTime)}`;
}
export function formatDaysOfWeek(daysOfWeek: string[]): string {
	const fullNames: { [key: string]: string } = {
		mon: 'monday',
		tues: 'tuesday',
		wed: 'wednesday',
		thurs: 'thursday',
		fri: 'friday',
		sat: 'saturday',
		sun: 'sunday'
	};

	if (daysOfWeek.length === 1) {
		return fullNames[daysOfWeek[0]];
	}

	const daysOrder = ['mon', 'tues', 'wed', 'thurs', 'fri', 'sat', 'sun'];

	const dayIndices = daysOfWeek.map(day => daysOrder.indexOf(day)).sort((a, b) => a - b);
	const isConsecutive = dayIndices.every((value, index) => index === 0 || value === dayIndices[index - 1] + 1);

	if (isConsecutive) {
		return `${daysOfWeek[0]}-${daysOfWeek[daysOfWeek.length - 1]}`;
	}

	return daysOfWeek.map(day => day.slice(0, 3)).join(', ');
}

export function formatDate(dates: string[]): string | null {
	if (!dates || dates.length === 0) {
		return null;
	}

	const upcomingDates = dates
		.map(date => new Date(date))
		.filter(date => date > new Date())
		.sort((a, b) => a.getTime() - b.getTime());

	if (upcomingDates.length === 0) {
		return null;
	}

	const nextDate = upcomingDates[0];
	return nextDate.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric'
	});
}

/** Local calendar midnight — avoids timezone drift when formatting display dates. */
function toLocalMidnight(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function formatMmDdYyyy(d: Date): string {
	const x = toLocalMidnight(d);
	const dd = String(x.getDate()).padStart(2, '0');
	const mm = String(x.getMonth() + 1).padStart(2, '0');
	const yyyy = String(x.getFullYear());
	return `${mm}/${dd}/${yyyy}`;
}

function dedupeDatesByCalendarDay(dates: Date[]): Date[] {
	const seen = new Set<string>();
	const out: Date[] = [];
	for (const d of dates) {
		const x = toLocalMidnight(d);
		const key = `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
		if (seen.has(key)) {
continue;
}
		seen.add(key);
		out.push(x);
	}
	return out.sort((a, b) => a.getTime() - b.getTime());
}

/** True when sorted dates are consecutive calendar days with no gaps (inclusive streak). */
function isConsecutiveCalendarDayStreak(sortedAsc: Date[]): boolean {
	if (sortedAsc.length <= 1) {
return true;
}
	for (let i = 1; i < sortedAsc.length; i++) {
		const prev = sortedAsc[i - 1];
		const cur = sortedAsc[i];
		const nextCal = new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1);
		if (
			cur.getFullYear() !== nextCal.getFullYear()
			|| cur.getMonth() !== nextCal.getMonth()
			|| cur.getDate() !== nextCal.getDate()
		) {
			return false;
		}
	}
	return true;
}

/** One row per date (or one row for a contiguous range). */
export function getNonRecurringDatesDetailLines(event: TDR.Event): string[] | null {
	const sched = event.eventSchedule ?? 'non-recurring';
	if (sched !== 'non-recurring') {
return null;
}

	const v = event.condition?.value;
	const dates = v?.dates;
	if (dates?.length) {
		const parsed = dates
			.map((d) => new Date(d))
			.filter((d) => !Number.isNaN(d.getTime()));
		if (!parsed.length) {
return null;
}

		const uniqueSorted = dedupeDatesByCalendarDay(parsed);
		if (!uniqueSorted.length) {
return null;
}

		if (isConsecutiveCalendarDayStreak(uniqueSorted)) {
			const first = uniqueSorted[0];
			const last = uniqueSorted[uniqueSorted.length - 1];
			if (first.getTime() === last.getTime()) {
				return [formatMmDdYyyy(first)];
			}
			return [`${formatMmDdYyyy(first)} - ${formatMmDdYyyy(last)}`];
		}

		return uniqueSorted.map((d) => formatMmDdYyyy(d));
	}

	const between = v?.betweenDates;
	if (between?.length === 2) {
		const start = new Date(between[0]);
		const end = new Date(between[1]);
		if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
return null;
}

		const s = toLocalMidnight(start);
		const e = toLocalMidnight(end);
		if (s.getTime() === e.getTime()) {
			return [formatMmDdYyyy(s)];
		}
		return [`${formatMmDdYyyy(s)} - ${formatMmDdYyyy(e)}`];
	}

	return null;
}

export const EventCard = ({ event }: { event: TDR.Event }) => {
	const [clicked, setClicked] = useState(false);
	const handleClick = () => {
		setClicked(!clicked);
	};

	const imageVariants = {
		default: { height: 270, opacity: 1 },
		hover: { height: 185, opacity: 1 },
		clicked: { height: 0, opacity: 0 }
	};

	const descriptionVariants = {
		default: { height: 0, opacity: 0 },
		hover: { height: 36, opacity: 1 },
		clicked: { height: 'auto', opacity: 1 }
	};

	const buttonVariants = {
		default: { opacity: 0 }, // Button hidden and slightly below its position
		hover: { opacity: 1 }, // Button becomes visible and moves into view
		clicked: { opacity: 1 } // Visible on click as well
	};

	const filePath = getEventImagePath(event.id);

	const timeInfo = event.condition?.value?.betweenTimes
		? formatBetweenTimes(event.condition.value.betweenTimes)
		: null;

	const dateLabel = getEventCardDateLabel(event);
	const guestLabel = getEventCardGuestLabel(event);
	return <motion.div
		onClick={handleClick}
		initial="default"
		whileHover={clicked ? 'clicked' : 'hover'} // Different behavior based on click state
		animate={clicked ? 'clicked' : 'default'}
		className={styles.wrapper}
	>
		<motion.img
			src={getEventCdnImageUri(filePath)}
			alt="Event Cover Image"
			className={styles.mediaContent}
			variants={imageVariants}
		/>
		<div className={styles.titleSection} >
			<div className={styles.title}>{event.displays[0].title}</div>
			<div className={styles.timeInfo}>
				{dateLabel && (
					<div className={styles.tag}>
						<DynamicIconsax iconName={'Calendar'} size={20} color={'#94969C'} />
						{dateLabel}
					</div>
				)}
				{timeInfo && (
					<div className={styles.tag}>
						<DynamicIconsax iconName={'Clock'} size={20} color={'#94969C'} />
						{timeInfo}
					</div>
				)}
				{guestLabel && (
					<div className={styles.tag}>
						<DynamicIconsax iconName={'Profile2User'} size={20} color={'#94969C'} />
						{guestLabel}
					</div>
				)}
			</div>
		</div>
		<motion.div className={styles.description} variants={descriptionVariants}>
			{event.displays[0].description}
		</motion.div>
		<motion.div
			style={{
				position: 'absolute',
				bottom: 0, // Always at the very bottom of the container
				left: '50%',
				transform: 'translateX(-50%)',
				textAlign: 'center',
				cursor: 'pointer',
				padding: '12px 0'
			}}
			variants={buttonVariants} // Use the buttonVariants for this element
		>
			{clicked ? <ArrowUp2 size="20" color="#CECFD2"/> : <ArrowDown2 size="20" color="#CECFD2"/>}
		</motion.div>
	</motion.div>;

};
